import { useEffect, useRef, useState } from 'react';
import { fileToBase64 } from '../lib/fileToBase64';
import { Dialog, Button, Input, Select, Textarea } from './ui';
import { api } from '../lib/api';
import type {
  LeaseAgencyPosture,
  LeaseAssetType,
  LeaseFieldFill,
  LeaseProposalGenerateInput,
  LeaseProposalPrefillPlan,
  LeaseRentStructure,
} from '../types';

/**
 * LP1.2 - the lease proposal generator.
 *
 * ONE component, every caller - the same arrangement `LoiGeneratorDialog` has, and for the same
 * reason: the letter is the same letter, and hand-kept copies of a legal document's form are the
 * drift this repo keeps paying for.
 *
 * ⛔⛔ IT WRITES NOTHING. The letter is built and downloaded. **Filing is LP1.5**, and the route
 * refuses a `deal_id` by name rather than accepting one and quietly not filing.
 *
 * ⭐⭐ LP1.3 BUILT ITS UPLOAD + PROMPT BOX (his decision 2), which LP1.2 deliberately left out
 * because a control that reads nothing is a button claiming to do what it cannot. It posts the
 * BYTES and reads them in memory - nothing is filed, nothing is stored, and `wrote: false` comes
 * back saying so.
 *
 * ⛔ AND NO PRINT BUTTON. The builder has no HTML renderer yet, and MM0's finding is exactly this
 * shape: "the same document arriving as two different letters depending on which button he
 * pressed", because the print path had none of the letterhead the .docx carries. A .docx that IS
 * the letter beats a print path that is not.
 */

/**
 * What a caller can suggest. ⛔ SUGGESTIONS, ALWAYS EDITABLE - a lease proposal carries his DRE
 * number to a counterparty, so the operator's eye is the gate on every one.
 */
export interface LeaseProposalPrefill {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  unit?: string | null;
  squareFeet?: number | null;
  apn?: string | null;
  assetType?: LeaseAssetType | null;
  landlordName?: string | null;
  tenantName?: string | null;
  /** The project or centre name for the Re: line. ⭐ Fill-and-flag, per his rule. */
  centreName?: string | null;
  ratePerSf?: number | null;
  /** LP1.3 - the rest of what a read can settle. Every one of these is still his to change. */
  monthlyRent?: number | null;
  termMonths?: number | null;
  commencementDate?: string | null;
  annualIncreasePercent?: number | null;
  rentStructure?: LeaseRentStructure | null;
  parkingRatio?: string | null;
  parkingSpaces?: number | null;
  /** What a document read could not settle, and why. Shown, never silently dropped. */
  gaps?: string[];
}

/**
 * ⭐ The form control each planned field belongs to. ⛔ ONE MAP, used by both paths that fill the
 * form - the `merged` defaults at mount and the imperative fill after a read - so the two cannot
 * disagree about where a value goes.
 */
const FIELD_INPUTS: Record<string, string> = {
  address: 'address',
  city: 'city',
  state: 'state',
  zip: 'zip',
  unit: 'unit',
  apn: 'apn',
  squareFeet: 'square_feet',
  landlordName: 'landlord_name',
  tenantName: 'tenant_name',
  centreName: 're_descriptor',
  ratePerSf: 'rate_per_sf',
  monthlyRent: 'monthly_rent',
  termMonths: 'term_months',
  commencementDate: 'commencement_date',
  annualIncreasePercent: 'annual_increase',
  rentStructure: 'rent_structure',
  parkingRatio: 'parking_ratio',
  parkingSpaces: 'parking_spaces',
  assetType: 'asset_type',
};

/** Field names as he would say them, for the sentence that tells him what was left blank. */
const FIELD_LABELS: Record<string, string> = {
  squareFeet: 'rentable SF', ratePerSf: 'base rate $/SF', monthlyRent: 'monthly rent',
  termMonths: 'term', commencementDate: 'commencement', annualIncreasePercent: 'annual increase',
  rentStructure: 'rent structure', address: 'address', city: 'city', state: 'state', zip: 'ZIP',
  unit: 'unit', apn: 'APN', landlordName: 'landlord', tenantName: 'tenant',
  centreName: 'centre name', parkingRatio: 'parking ratio', parkingSpaces: 'parking spaces',
  assetType: 'property type',
};

const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? field;

/**
 * The plan's fills, flattened into the prefill the form already understands.
 * ⛔⛔ THE `gaps` IT PRODUCES ARE THE HELD FIELDS, not every unfilled one. A read that names twelve
 * blanks - most of them things no listing ever states - buries the one that matters, and a notice
 * that cries wolf is a notice he stops reading.
 */
export const planToPrefill = (plan?: LeaseProposalPrefillPlan): LeaseProposalPrefill => {
  if (!plan) return {};
  const value = <T,>(fill?: LeaseFieldFill<T>): T | undefined => fill?.value;
  return {
    address: value(plan.address), city: value(plan.city), state: value(plan.state),
    zip: value(plan.zip), unit: value(plan.unit), apn: value(plan.apn),
    squareFeet: value(plan.squareFeet), landlordName: value(plan.landlordName),
    tenantName: value(plan.tenantName), centreName: value(plan.centreName),
    ratePerSf: value(plan.ratePerSf), monthlyRent: value(plan.monthlyRent),
    termMonths: value(plan.termMonths), commencementDate: value(plan.commencementDate),
    annualIncreasePercent: value(plan.annualIncreasePercent),
    rentStructure: value(plan.rentStructure), parkingRatio: value(plan.parkingRatio),
    parkingSpaces: value(plan.parkingSpaces), assetType: value(plan.assetType),
    gaps: plan.held.map((h) => fieldLabel(h.field)),
  };
};

/** ⛔ Only a real ISO date may reach a `type="date"` input - anything else silently blanks it. */
const isIsoDate = (v: unknown): boolean => /^\d{4}-\d{2}-\d{2}$/.test(String(v ?? ''));

const toBase64 = fileToBase64;

export interface LeaseProposalGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: LeaseProposalPrefill;
  /**
   * LP1.5 - the property this dialog is open on. Present, the operator may tick "file it";
   * absent, the letter only ever downloads and the control is not offered at all.
   * ⛔ A control that cannot do what it says is worse than no control - LP1.2's ruling about the
   * upload box, applied to its own feature.
   */
  propertyId?: string | null;
  /** How the property reads, for the sentence beside the box. */
  propertyLabel?: string | null;
}

interface GeneratedLeaseProposal {
  proposal_text: string;
  docx_base64: string;
  filename: string;
  title: string;
  flags: { field: string; note: string }[];
  sections: { number: number; title: string }[];
  filed: boolean;
  /** LP1.5 - the `file_links` row. ⛔ Removing a filed document is an UNLINK, never a delete. */
  link_id: string | null;
  /** ⛔ WHERE it went, in words. "Filed" with no destination hides its own contents. */
  filed_on: string | null;
  already_filed: boolean;
}

const ASSET_TYPES: { value: LeaseAssetType; label: string }[] = [
  { value: 'office_suite', label: 'Office / flex suite in a managed project' },
  { value: 'industrial_flex', label: 'Freestanding industrial or flex' },
  { value: 'industrial_condo', label: 'Industrial condo unit (HOA)' },
  { value: 'retail_center', label: 'Retail or industrial centre unit (NNN)' },
  { value: 'ios_yard', label: 'IOS / trucking yard' },
];

const POSTURES: { value: LeaseAgencyPosture; label: string }[] = [
  { value: 'dual_agency', label: 'Dual agency - Apex acts for both sides' },
  { value: 'exclusive_tenant_rep', label: 'Exclusive tenant representation' },
];

const RENT_STRUCTURES: { value: LeaseRentStructure; label: string }[] = [
  { value: 'modified_gross', label: 'Modified Gross' },
  { value: 'nnn', label: 'Triple Net (NNN)' },
  { value: 'gross', label: 'Gross' },
];

/** "September 18, 2026" in Pacific time - the letter's own date line. */
const laLongDate = (d: Date): string =>
  d.toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles', month: 'long', day: 'numeric', year: 'numeric',
  });

export default function LeaseProposalGeneratorDialog({
  open,
  onClose,
  prefill,
  propertyId,
  propertyLabel,
}: LeaseProposalGeneratorDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedLeaseProposal | null>(null);

  /** LP1.3 - the read box. */
  const [files, setFiles] = useState<File[]>([]);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [plan, setPlan] = useState<LeaseProposalPrefillPlan | null>(null);
  /**
   * 🚨🚨🚨 WHAT THE READ FOUND AND DID **NOT** PUT IN THE FORM, because the box already held
   *    something else. Found by opening the dialog on one property and attaching a report for
   *    another: every CoStar field that had no counterpart in the record filled, and every field
   *    the record had already filled kept the record's value - so the form ended up describing the
   *    LANDLORD, SUITE and CENTRE of 1801 Excise Ave beside the ADDRESS, APN, ZIP and square
   *    footage of 4761-4769 E State St, and nothing on the screen said a word about it.
   * ⛔⛔ Protecting his values is right. Saying nothing about the ones we discarded is not - it is
   *    the wrong-document failure wearing a form's clothes.
   */
  const [notApplied, setNotApplied] = useState<{ field: string; fromDocument: string; inForm: string }[]>([]);
  const [sources, setSources] = useState<{ filename: string; numPages: number; pagesWithText: number; factCount: number }[]>([]);

  const formRef = useRef<HTMLFormElement>(null);
  /**
   * ⛔⛔ WHAT A PREVIOUS READ PUT IN EACH BOX, so a second read may replace its own suggestion and
   * NEVER what he typed over it. Seeded with the selects' mount defaults, because a default he has
   * not looked at is not a choice he made.
   */
  const appliedRef = useRef<Record<string, string>>({});

  /**
   * ⛔⛔⛔ EVERY INPUT READS `merged`, NEVER THE BARE PREFILL. The 84th's finding: eleven green
   * guards sat above inputs that had never filled, because the notices read one object and the
   * inputs read another. ⭐ LP1.3's document plan merges HERE - one object, one source of values.
   * ⚠️ `merged` only reaches the DOM at MOUNT, because these inputs are uncontrolled; a read that
   * lands afterwards is applied by `applyPlanToForm` below, from this same object.
   *
   * ⭐ THE PREFILL COMES LAST, so this object agrees with the boxes. `applyPlanToForm` keeps a
   *   value the caller supplied and lists the document's under "your form already had these"; with
   *   the spread the other way round `merged` said the opposite of what the form showed. Nothing
   *   on screen depended on it - see the measurement below - but an object that disagrees with the
   *   DOM is the 84th's defect waiting for its next reader.
   * ⭐⭐ MEASURED, because I had the mechanism wrong first: a changed `defaultValue` reaches an
   *   input that has **no value yet** ("" -> "Ontario" lands) and NOT one that already shows
   *   something ("91762" -> "91761" changes the attribute and leaves the box at 91762). So React
   *   never overwrites what is already there, and `applyPlanToForm` is what fills a box
   *   deliberately. Pinned in `LeaseProposalGeneratorDialog.test.tsx`.
   */
  const fromPlan = planToPrefill(plan ?? undefined);
  const merged: LeaseProposalPrefill | undefined = {
    ...fromPlan,
    ...prefill,
    /** ⛔ The gaps are the PLAN's - a caller's prefill has nothing to say about what a read settled. */
    ...(plan ? { gaps: fromPlan.gaps } : {}),
  };

  /** ⛔ Resets on close: a letter's values must not survive into the next letter. */
  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
      setPlan(null);
      setFiles([]);
      setSources([]);
      setReadError(null);
      setNotApplied([]);
      appliedRef.current = {};
      return;
    }
    /**
     * ⭐⭐ A DEFAULT HE HAS NOT LOOKED AT IS NOT A CHOICE HE MADE. The two selects always carry a
     * value, so the "never overwrite what he typed" rule would have frozen the property type at
     * `industrial_flex` forever - and property type is what decides which sections the letter
     * carries. Seeding their mount values here means a read may replace an untouched default, and
     * the moment he picks something the value stops matching and his choice is protected.
     */
    appliedRef.current = { asset_type: String(prefill?.assetType ?? 'industrial_flex') };
  }, [open, prefill]);

  if (!open) return null;

  /**
   * ⛔⛔⛔ A READ FILLS THE BLANKS. IT NEVER OVERWRITES WHAT HE TYPED.
   *
   * These inputs are UNCONTROLLED, so React will not push a new `defaultValue` into a box that is
   * already on screen - and the two ways of forcing it are both wrong. Remounting the form is the
   * 88th's defect exactly (it threw away everything he had typed); making thirty fields controlled
   * to solve a one-shot fill is a rewrite of the working half.
   * ⭐ So the fill is imperative and conservative: a box that is empty, or that still holds what the
   *   LAST read put there, takes the new value. A box he has touched keeps his value, every time.
   *
   * ⚠️⚠️ AND A SABOTAGE SHOWED WHY THIS CANNOT BE LEFT TO REACT. Mis-wiring `city` here changed
   *    nothing on screen: React writes `defaultValue` as the `value` ATTRIBUTE, and **an input with
   *    no value yet** mirrors it - so the re-render happened to fill an empty box. ⭐ Measured
   *    precisely afterwards: that is ALL it does. An input that already shows something keeps
   *    showing it while the attribute changes underneath, and a `<select>` React only sets at
   *    mount. **Pin the outcome, never the mechanism**: this path is what makes the behaviour the
   *    same for every control and every starting state.
   */
  const applyPlanToForm = (next: LeaseProposalPrefillPlan): void => {
    const form = formRef.current;
    if (!form) return;
    const values = planToPrefill(next);
    const kept: { field: string; fromDocument: string; inForm: string }[] = [];

    for (const [field, inputName] of Object.entries(FIELD_INPUTS)) {
      const value = (values as Record<string, unknown>)[field];
      if (value === undefined || value === null || value === '') continue;
      if (field === 'commencementDate' && !isIsoDate(value)) continue;

      const el = form.elements.namedItem(inputName) as HTMLInputElement | HTMLSelectElement | null;
      if (!el || !('value' in el)) continue;

      const current = String(el.value ?? '').trim();
      const applied = appliedRef.current[inputName] ?? '';
      if (current !== '' && current !== applied) {
        // ⛔ His value wins - and the one it beat is NAMED rather than dropped in silence.
        if (current.toLowerCase() !== String(value).toLowerCase()) {
          kept.push({ field, fromDocument: String(value), inForm: current });
        }
        continue;
      }

      el.value = String(value);
      appliedRef.current[inputName] = String(value);
    }
    setNotApplied(kept);
  };

  const readSources = async (): Promise<void> => {
    if (reading) return;
    const form = formRef.current;
    const notesEl = form?.elements.namedItem('notes') as HTMLTextAreaElement | null;
    const notes = (notesEl?.value ?? '').trim();

    if (files.length === 0 && !notes) {
      setReadError('Attach a document or type the terms - there is nothing to read.');
      return;
    }

    setReading(true);
    setReadError(null);
    try {
      const documents = await Promise.all(
        files.map(async (file) => ({ filename: file.name, base64: await toBase64(file) })),
      );
      const res = await api.readLeaseProposalDocuments({ documents, notes });
      setPlan(res.read.plan);
      setSources(res.read.sources.map((s) => ({
        filename: s.filename, numPages: s.numPages, pagesWithText: s.pagesWithText, factCount: s.factCount,
      })));
      applyPlanToForm(res.read.plan);
    } catch (err) {
      setReadError(err instanceof Error ? err.message : 'Could not read those documents');
    } finally {
      setReading(false);
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    const fd = new FormData(e.currentTarget);
    const g = (n: string): string => ((fd.get(n) as string) ?? '').trim();
    const num = (n: string): number | undefined => {
      const v = parseFloat(g(n));
      return Number.isFinite(v) && v > 0 ? v : undefined;
    };
    const lines = (n: string): string[] =>
      g(n).split('\n').map((l) => l.trim()).filter(Boolean);

    // "5:00 p.m. on October 2, 2026" - the form his own letters use.
    const [ey, em, ed] = g('expiration_date').split('-').map(Number);
    const expiration = ey && em && ed
      ? `5:00 p.m. on ${laLongDate(new Date(ey, em - 1, ed))}`
      : '';

    const [cy, cm, cd] = g('commencement_date').split('-').map(Number);
    const commencementDate = cy && cm && cd ? laLongDate(new Date(cy, cm - 1, cd)) : '';

    /**
     * "2,3" -> [2, 3]. ⛔ Only sent when something was typed: an empty list means no abatement,
     * and the builder drops RENT ABATEMENT and RENT COMMENCEMENT together rather than printing
     * an abatement of nothing.
     */
    const abatementMonths = g('abatement_months')
      .split(/[,\s]+/)
      .map((v) => parseInt(v, 10))
      .filter((v) => Number.isInteger(v) && v > 0);

    const moveInFees = lines('move_in_fees')
      .map((line) => {
        const m = line.match(/^(.*?)[\s|]*[-|]\s*\$?([\d,.]+)$/);
        if (!m) return null;
        const amount = parseFloat(m[2].replace(/,/g, ''));
        return Number.isFinite(amount) ? { label: m[1].trim(), amount } : null;
      })
      .filter((f): f is { label: string; amount: number } => f !== null);

    const renewalOptions = num('renewal_options');
    const renewalYears = num('renewal_years');

    const proposal: LeaseProposalGenerateInput = {
      letterDate: laLongDate(new Date()),
      assetType: g('asset_type') as LeaseAssetType,
      recipient: {
        name: g('recipient_name'),
        company: g('recipient_company') || null,
        addressLines: lines('recipient_address'),
        salutationName: g('salutation_name') || null,
      },
      landlordName: g('landlord_name'),
      tenant: {
        name: g('tenant_name'),
        shortName: g('tenant_short') || null,
        principal: g('tenant_principal') || null,
        affiliate: g('tenant_affiliate') || null,
      },
      premises: {
        address: g('address'),
        city: g('city'),
        state: g('state') || 'CA',
        zip: g('zip') || null,
        unit: g('unit') || null,
        squareFeet: num('square_feet') ?? null,
        spaceType: g('space_type') || null,
        locationWithin: g('location_within') || null,
        apn: g('apn') || null,
        exhibit: g('exhibit') || null,
      },
      reLineDescriptor: g('re_descriptor') || null,
      terms: {
        termMonths: num('term_months') ?? 0,
        commencementDate,
        rent: {
          squareFeet: num('square_feet') ?? null,
          ratePerSf: num('rate_per_sf') ?? null,
          monthlyRent: num('monthly_rent') ?? null,
          annualIncreasePercent: num('annual_increase') ?? null,
        },
        rentStructure: (g('rent_structure') || null) as LeaseRentStructure | null,
        renewal: renewalOptions && renewalYears
          ? { options: renewalOptions, years: renewalYears, noticeDays: num('renewal_notice') ?? 90 }
          : null,
        abatementMonths: abatementMonths.length ? abatementMonths : null,
        abatementConcession: num('abatement_concession') ?? null,
        firstMonthPrepaid: fd.get('first_month_prepaid') === 'on',
        cam: num('cam_rate') || num('cam_monthly')
          ? {
            ratePerSf: num('cam_rate') ?? null,
            monthly: num('cam_monthly') ?? null,
            capPercent: num('cam_cap') ?? null,
          }
          : null,
        nnn: num('nnn_rate') || num('nnn_monthly')
          ? { ratePerSf: num('nnn_rate') ?? null, monthly: num('nnn_monthly') ?? null }
          : null,
        associationFeeMonthly: num('association_fee') ?? null,
        securityDeposit: num('security_deposit') ?? null,
        moveInFees: moveInFees.length ? moveInFees : null,
        deliveryItems: lines('delivery_items'),
        cityApprovalRefundDays: num('city_approval_days') ?? null,
        permittedUse: g('permitted_use') || null,
        permittingAuthority: g('permitting_authority') || null,
        parkingSpaces: num('parking_spaces') ?? null,
        parkingRatio: g('parking_ratio') || null,
        guarantyBurnOffMonths: num('guaranty_months') ?? null,
        expiration,
      },
      brokerage: {
        posture: g('posture') as LeaseAgencyPosture,
        commissionPercent: num('commission') ?? null,
        landlordsBrokerage: g('landlords_brokerage') || null,
      },
      /**
       * ⭐ HIS SETTING, and the checkbox is checked by default because the builder's default is
       * true. An unticked box means "let it break where the page breaks", which is what he asked
       * for: *"i dont want the first page to be half empty."*
       */
      keepRentTableTogether: fd.get('keep_rent_table') === 'on',
    };

    /**
     * ⛔⛔ THE ONE PLACE A WRITE CAN HAPPEN, AND IT IS HIS TICK THAT CAUSES IT. `file_on_property_id`
     * is sent only when the box is on; the route files only when it arrives. Nothing about being
     * on a property page files anything by itself.
     */
    const fileIt = fd.get('file_on_property') === 'on' && propertyId ? propertyId : null;

    try {
      const res = await api.generateLeaseProposal(proposal, fileIt);
      setResult(res.proposal);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate the lease proposal');
    } finally {
      setBusy(false);
    }
  };

  const downloadDocx = () => {
    if (!result?.docx_base64) return;
    const bytes = Uint8Array.from(atob(result.docx_base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /**
   * ⛔ THE SHARED `Dialog` PRIMITIVE, not chrome of its own. `Dialog.oo5c.test.tsx` enforces it
   * and caught the first version of this file, which hand-rolled its own full-screen overlay - the
   * exact thing that guard exists to stop. Its three older siblings (brochure, LOI, listing
   * proposal) are on its exclusion list as grandfathered MM1 generators; a NEW one has no reason
   * to join them.
   * ⚠️ AND THAT GUARD IS A SOURCE-TEXT SCAN, so it also fired on the COMMENT naming the class it
   * forbids - a guard tripped by a description of the defect rather than the defect. Say it in
   * words here rather than weakening a guard that was doing its job.
   * ⭐ `z-[1200]`, NOT the default z-50: this mounts on the property page, whose Leaflet map gives
   * its own panes z-index 400-650. At z-50 the map draws straight THROUGH the dialog - measured on
   * the LOI, which carries the same override for the same reason.
   * ⭐ The primitive also owns the MM1 scroll chain, so a form this long never scrolls its own
   * title or its Generate button out of reach.
   */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Proposal to Lease"
      description={
        propertyId
          ? 'Nothing is written until you press Generate, and only if you asked for it to be filed.'
          : 'Nothing is written until you press Generate, and this downloads rather than files.'
      }
      size="xl"
      zClassName="z-[1200]"
    >
      <div>
          {error && (
            <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {/**
            * ⛔⛔ WHAT A DOCUMENT READ COULD NOT SETTLE, NAMED. Without this a withheld field reads
            * as a broken feature rather than a protected one, which is how a safety rule gets
            * switched off. (LP1.3 fills this; the surface exists so it cannot be forgotten.)
            */}
          {merged?.gaps && merged.gaps.length > 0 && (
            <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              Left for you, because the source did not settle them: {merged.gaps.join(', ')}.
            </div>
          )}

          {/**
            * ⛔⛔ THE FORM IS HIDDEN, NEVER UNMOUNTED - found by LOOKING, and no test could see it.
            *
            * These inputs are UNCONTROLLED (`defaultValue`), so unmounting the form throws away
            * every value he typed. Generating a letter, spotting one wrong figure and pressing
            * "Back to the form" put him in front of an EMPTY form with thirty-odd fields to retype
            * - only the record's own prefills survived, which made it look like it had half-worked.
            * Toggling `hidden` keeps the DOM nodes, so his typing is still there.
            *
            * ⚠️ Every test renders fresh and submits once, so all fourteen stayed green through it.
            */}
          {result && (
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <Button onClick={downloadDocx}>Download Word (.docx)</Button>
                <Button variant="ghost" onClick={() => setResult(null)}>Back to the form</Button>
                <span className="text-sm text-gray-600">
                  {result.sections.length} sections.{' '}
                  {result.filed
                    ? (result.already_filed
                      ? `This letter was already in ${result.filed_on ?? 'this property'}'s documents - nothing was written twice.`
                      : `Filed on ${result.filed_on ?? 'this property'} as a Lease proposal.`)
                    : 'Nothing was filed - this downloads.'}
                </span>
              </div>

              {result.flags.length > 0 && (
                <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="font-semibold">Check these before you send it:</p>
                  <ul className="mt-1 list-disc pl-5">
                    {result.flags.map((f) => (
                      <li key={f.field}><span className="font-medium">{f.field}</span> - {f.note}</li>
                    ))}
                  </ul>
                </div>
              )}

              <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded bg-gray-50 p-4 text-sm">
                {result.proposal_text}
              </pre>
            </div>
          )}

          <form ref={formRef} onSubmit={submit} className="space-y-6" hidden={result !== null}>
              {/**
                * ⭐⭐ HIS DECISION 2: the button door takes documents too. *"i will probably do this
                * in chat most of time but if i push the button to create it, id like to be able to
                * add documents and give it a prompt."*
                * ⛔⛔ IT READS AND SUGGESTS. Nothing is filed, nothing is stored, and nothing is
                * written until Generate.
                */}
              <fieldset className="rounded border border-blue-200 bg-blue-50/60 p-3">
                <legend className="px-1 text-sm font-semibold text-gray-900">
                  Read it from a document
                </legend>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700" htmlFor="lease-proposal-sources">
                      CoStar report, a model proposal - PDFs, up to three
                    </label>
                    <input
                      id="lease-proposal-sources"
                      type="file"
                      multiple
                      accept="application/pdf,.pdf"
                      className="mt-1 block w-full text-sm"
                      onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 3))}
                    />
                    {files.length > 0 && (
                      <p className="mt-1 text-xs text-gray-600">
                        {files.map((f) => f.name).join(', ')}
                      </p>
                    )}
                  </div>

                  <Textarea
                    name="notes"
                    label="And the terms, in your own words"
                    rows={3}
                    className="col-span-full"
                    placeholder={'Three year term at $2.00 a foot starting December 1, 3% annual increases. Tenant is Capital Equity Partners.'}
                    hint="What you type here outranks the documents - they are a listing, you are the deal."
                  />

                  <div className="flex flex-wrap items-center gap-3">
                    <Button type="button" variant="ghost" onClick={readSources} disabled={reading}>
                      {reading ? 'Reading...' : 'Read these'}
                    </Button>
                    <span className="text-xs text-gray-600">
                      Nothing is filed or stored - it reads what you attach and fills the blanks
                      below. Anything you have typed is left alone.
                    </span>
                  </div>

                  {readError && (
                    <div role="alert" className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800">
                      {readError}
                    </div>
                  )}

                  {sources.length > 0 && (
                    <p className="text-xs text-gray-700">
                      {sources.map((s) => (
                        `${s.filename}: ${s.factCount} figures, ${s.pagesWithText} of ${s.numPages} pages with a text layer`
                      )).join(' | ')}
                    </p>
                  )}
                </div>
              </fieldset>

                      {plan && <ReadFindings plan={plan} notApplied={notApplied} />}

              <Section title="The deal">
                <Select
                  name="asset_type"
                  label="Property type *"
                  required
                  defaultValue={merged?.assetType ?? 'industrial_flex'}
                  options={ASSET_TYPES}
                />
                <p className="col-span-full -mb-1 text-xs text-gray-600">
                  Property type chooses which optional sections the letter carries. It never
                  rewrites wording.
                </p>
                <Select
                  name="posture"
                  label="Agency posture *"
                  required
                  defaultValue="dual_agency"
                  options={POSTURES}
                />
                <p className="col-span-full -mt-2 text-xs text-gray-600">
                  Dual agency prints the broker clause and the landlord-protective close that are in
                  19 of your 19 classic letters. Exclusive tenant rep drops both and states the
                  commission, as your two TCA letters do. It is a per-deal choice, not the template's.
                </p>
              </Section>

              <Section title="Addressed to">
                <Input name="recipient_name" required label="Recipient *" placeholder="Mr. Adolfo De La Herran" />
                <Input name="recipient_company" label="Company" placeholder="De La Herran Revocable Living Family Trust" />
                <Input name="salutation_name" label='"Dear ___:"' placeholder="Mr. De La Herran" hint="Left blank, the recipient's name is used exactly as typed." />
                <Textarea name="recipient_address" label="Address lines" rows={2} placeholder={'Rancho Cucamonga, CA'} />
              </Section>

              <Section title="Parties">
                <Input name="landlord_name" required label="Landlord *" defaultValue={merged?.landlordName ?? ''} placeholder="1801 EXCISE AVENUE LLC" />
                <Input name="tenant_name" required label="Tenant *" defaultValue={merged?.tenantName ?? ''} placeholder="Capital Equity Partners dba The Corporate Advocate" />
                <Input name="tenant_short" label="Tenant short form" placeholder="TCA" hint='Defined in section 2 and used afterwards.' />
                <Input name="tenant_principal" label="Principal" placeholder="Joey Sanchez" />
                <Input name="tenant_affiliate" label="Affiliate occupying with the Tenant" placeholder="Sync Technologies, Inc." />
              </Section>

              <Section title="Premises">
                <Input name="address" required label="Address *" defaultValue={merged?.address ?? ''} placeholder="1801 Excise Avenue" />
                <Input name="city" required label="City *" defaultValue={merged?.city ?? ''} placeholder="Ontario" />
                <Input name="state" label="State" defaultValue={merged?.state ?? 'CA'} />
                <Input name="zip" label="ZIP" defaultValue={merged?.zip ?? ''} />
                <Input name="unit" label="Unit / suite" defaultValue={merged?.unit ?? ''} placeholder="Suite 114" />
                <Input name="square_feet" type="number" step="1" label="Rentable SF" defaultValue={merged?.squareFeet ?? ''} hint="Required when rent is quoted per SF. A yard leased by the lot needs none." />
                <Input name="space_type" label="Space type" placeholder="office space" />
                <Input name="location_within" label="Position within the project" placeholder="located on the first floor of the Acco Airport Center" />
                <Input name="apn" label="APN" defaultValue={merged?.apn ?? ''} />
                <Input name="exhibit" label="Exhibit" placeholder="Exhibit A" />
                <Input name="re_descriptor" label="Re: line descriptor" defaultValue={merged?.centreName ?? ''} placeholder="Acco Airport Center, +/- 1,440 SF" hint="Yours to type - it is never written for you." />
              </Section>

              <Section title="Term and rent">
                <Input name="term_months" type="number" step="1" required label="Term in months *" defaultValue={merged?.termMonths ?? ''} placeholder="36" />
                <Input name="commencement_date" type="date" required label="Lease commencement *" defaultValue={merged?.commencementDate ?? ''} />
                <Select name="rent_structure" label="Rent structure" defaultValue={merged?.rentStructure ?? ''} placeholder="Default for this property type" options={RENT_STRUCTURES} />
                <Input name="rate_per_sf" type="number" step="0.01" label="Base rate $/SF/month" defaultValue={merged?.ratePerSf ?? ''} placeholder="2.00" />
                <Input name="monthly_rent" type="number" step="0.01" label="Flat monthly rent" defaultValue={merged?.monthlyRent ?? ''} placeholder="4000" hint="For a lease not quoted per SF, like a yard. Use one or the other." />
                <Input name="annual_increase" type="number" step="0.1" label="Annual increase %" defaultValue={merged?.annualIncreasePercent ?? ''} placeholder="3" hint="Blank or 0 prints a fixed rent with no increases." />
                <Input name="expiration_date" type="date" required label="This proposal expires *" />
              </Section>

              <Section title="Concessions and deposits">
                <Input name="abatement_months" label="Abated months" placeholder="2, 3" hint="Which months of the Term are free. Blank means none." />
                <Input name="abatement_concession" type="number" step="0.01" label="Concession value $" placeholder="5760" />
                <Input name="security_deposit" type="number" step="0.01" label="Security deposit $" placeholder="2880" />
                <Textarea name="move_in_fees" label="Move-in fees" rows={3} placeholder={"First Month's Base Rent (CAM included) - 2880\nSecurity Deposit - 2880"} hint="One per line, label then the amount. The total is added for you." />
                <label className="col-span-full flex items-center gap-2 text-sm">
                  <input type="checkbox" name="first_month_prepaid" />
                  The first month&apos;s rent is collected at execution
                </label>
              </Section>

              <Section title="Charges">
                <Input name="cam_rate" type="number" step="0.01" label="CAM $/SF/month" placeholder="0.07" />
                <Input name="cam_monthly" type="number" step="0.01" label="CAM $/month" placeholder="132.72" />
                <Input name="cam_cap" type="number" step="0.1" label="Controllable CAM cap %" placeholder="5" />
                <Input name="nnn_rate" type="number" step="0.01" label="NNN $/SF/month" placeholder="0.35" />
                <Input name="nnn_monthly" type="number" step="0.01" label="NNN $/month" placeholder="352.80" />
                <Input name="association_fee" type="number" step="0.01" label="Association fee $/month" placeholder="150" />
              </Section>

              <Section title="Delivery, use and options">
                <Textarea name="delivery_items" label="Landlord's Work" rows={4} className="col-span-full" placeholder={'Fresh paint throughout the Premises\nProfessionally clean all carpeting'} hint="One per line. For a yard these are the yard items, and the whole block changes with it." />
                <Textarea name="permitted_use" label="Permitted use" rows={2} className="col-span-full" placeholder="General office use for Tenant's consulting and administrative staff." />
                <Input name="permitting_authority" label="Permitting authority" placeholder="City of San Bernardino" hint="Present, a permitting contingency is written." />
                <Input name="city_approval_days" type="number" step="1" label="City-approval refund days" placeholder="10" />
                <Input name="parking_spaces" type="number" step="1" label="Parking spaces" defaultValue={merged?.parkingSpaces ?? ''} placeholder="6" />
                <Input name="parking_ratio" label="Parking ratio" defaultValue={merged?.parkingRatio ?? ''} placeholder="4.0 / 1,000 SF" />
                <Input name="guaranty_months" type="number" step="1" label="Guaranty burn-off months" placeholder="24" />
                <Input name="renewal_options" type="number" step="1" label="Renewal options" placeholder="1" />
                <Input name="renewal_years" type="number" step="1" label="Years per option" placeholder="3" />
                <Input name="renewal_notice" type="number" step="1" label="Notice days" placeholder="90" />
              </Section>

              <Section title="Brokerage">
                <Input name="landlords_brokerage" label="Landlord's brokerage" placeholder="Walker Perry Real Estate Brokerage" />
                <Input name="commission" type="number" step="0.1" label="Commission %" placeholder="4" hint="Tenant-rep letters state it; dual-agency letters say 'per separate agreement'." />
              </Section>

              {/**
                * ⛔⛔⛔ LP1.5 - THE ONLY CONTROL IN THIS DIALOG THAT CAUSES A WRITE, and it is OFF.
                * The letter downloads unless he ticks this. A dialog that always sits on a property
                * could have filed on the id it already has, and then every Generate would have
                * written a client-visible document nobody chose to write.
                * ⭐ It is offered ONLY when there is a property to file on - a control that cannot
                * do what it says is worse than no control (LP1.2's own ruling about the upload box).
                */}
              {propertyId && (
                <Section title="Filing">
                  <label className="col-span-full flex items-start gap-2 text-sm">
                    <input type="checkbox" name="file_on_property" className="mt-1" />
                    <span>
                      File it on {propertyLabel || 'this property'}
                      <span className="block text-xs text-gray-600">
                        Off, this downloads and writes nothing. On, the .docx is also saved to this
                        property&apos;s documents as a Lease proposal - and you still get the
                        download. Removing it later unlinks it; the file itself is kept.
                      </span>
                    </span>
                  </label>
                </Section>
              )}

              <Section title="Layout">
                <label className="col-span-full flex items-start gap-2 text-sm">
                  <input type="checkbox" name="keep_rent_table" defaultChecked className="mt-1" />
                  <span>
                    Keep the rent table whole on one page
                    <span className="block text-xs text-gray-600">
                      On, the table and its total never separate - but the whole section moves down
                      if it does not fit, which can leave a page part empty. Off, it breaks where the
                      page breaks.
                    </span>
                  </span>
                </label>
              </Section>

              <div className="flex items-center gap-3 border-t pt-4">
                <Button type="submit" disabled={busy}>
                  {busy ? 'Generating...' : 'Generate'}
                </Button>
                <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
                <span className="text-sm text-gray-600">
                  Nothing is written until you press Generate
                  {propertyId ? ', and only if you ticked Filing above.' : ', and this downloads rather than files.'}
                </span>
              </div>
        </form>
      </div>
    </Dialog>
  );
}

/**
 * ⛔⛔⛔ WHAT THE READ COULD NOT SETTLE, AND WHY - the detail under the notice the dialog already
 * shows. A withheld field with no explanation reads as a broken feature rather than a protected
 * one, which is how a safety rule gets switched off.
 *
 * ⭐ Every held field names BOTH figures and quotes both, because the point is not that the machine
 * refused: it is that he can see the two numbers and decide in five seconds.
 */
function ReadFindings({
  plan,
  notApplied,
}: {
  plan: LeaseProposalPrefillPlan;
  notApplied: { field: string; fromDocument: string; inForm: string }[];
}) {
  const { held, contradictions, supersededByYourNotes, flags } = plan;
  if (!held.length && !contradictions.length && !supersededByYourNotes.length && !flags.length
    && !notApplied.length) return null;

  /**
   * 🚨🚨🚨 IS THIS DOCUMENT EVEN ABOUT THIS PROPERTY? The loudest thing on the screen when it is
   *    not, because the failure it prevents is the worst one this dialog can produce: a letter
   *    naming ONE building's landlord, suite and centre over ANOTHER building's address, APN and
   *    square footage - every field carrying a verified quote, and nothing saying they came from
   *    two different properties.
   * ⭐ Found by attaching a real report on the wrong property's page and LOOKING at the form.
   */
  const differentProperty = notApplied.find((n) => n.field === 'address');

  return (
    <div className="space-y-3">
      {differentProperty && (
        <div role="alert" className="rounded border-2 border-red-500 bg-red-50 p-3 text-sm text-red-900">
          <p className="font-semibold">
            This document is about a different address from the one on your form.
          </p>
          <p className="mt-1">
            It reads <span className="font-semibold">{differentProperty.fromDocument}</span>; your form
            says <span className="font-semibold">{differentProperty.inForm}</span>. Your address was
            kept - but check every field below before you send, because the rest of this letter may
            now describe two buildings.
          </p>
        </div>
      )}

      {notApplied.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Your form already had these, so the document was not used:</p>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {notApplied.map((n) => (
              <li key={n.field}>
                <span className="font-medium">{fieldLabel(n.field)}</span> - yours:{' '}
                <span className="font-semibold">{n.inForm}</span>; the document:{' '}
                <span className="font-semibold">{n.fromDocument}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {held.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Left blank, because the sources did not settle them:</p>
          <ul className="mt-1 space-y-2">
            {held.map((h) => (
              <li key={h.field}>
                <span className="font-medium">{fieldLabel(h.field)}</span> - {h.reason}.
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {h.candidates.map((c, i) => (
                    <li key={`${h.field}-${i}`}>
                      <span className="font-semibold">{String(c.value)}</span>
                      {c.quote ? <> - &ldquo;{c.quote}&rdquo;</> : null}
                      {c.page ? <> (page {c.page}{c.from ? `, ${c.from}` : ''})</> : null}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {contradictions.map((c, i) => (
        <div key={`contra-${i}`} className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-semibold">{c.from} contradicts itself.</p>
          <p className="mt-1">{c.statement}.</p>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {c.quotes.map((q, j) => <li key={`q-${j}`}>&ldquo;{q.quote}&rdquo; (page {q.page})</li>)}
          </ul>
        </div>
      ))}

      {supersededByYourNotes.length > 0 && (
        <div className="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800">
          <p className="font-semibold">Your notes overrode the documents here:</p>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {supersededByYourNotes.map((s) => (
              <li key={s.field}>
                <span className="font-medium">{fieldLabel(s.field)}</span> - the document said{' '}
                {s.candidates.map((c) => String(c.value)).join(', ')}
                {s.candidates[0]?.quote ? <> (&ldquo;{s.candidates[0].quote}&rdquo;)</> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {flags.length > 0 && (
        <div className="rounded border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
          <p className="font-semibold">Filled, but look at these:</p>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {flags.map((f) => (
              <li key={f.field}><span className="font-medium">{fieldLabel(f.field)}</span> - {f.note}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-semibold text-gray-900">{title}</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
